
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';

const Dashboard: React.FC = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();

  if (!context) return null;

  const { projects, clients, tasks, transactions, expenses } = context;

  const activeProjects = projects.filter(p => p.status === 'Active').length;
  const pendingTasks = tasks.filter(t => t.status !== 'Done').length;
  const totalRevenue = transactions.filter(t => t.status === 'Paid').reduce((acc, curr) => acc + curr.amount, 0);
  const totalExpenses = expenses.filter(e => e.status === 'Paid').reduce((acc, curr) => acc + curr.amount, 0);
  const balance = totalRevenue - totalExpenses;

  const clientGoal = 50;
  const clientProgress = clients.length > 0 ? Math.min((clients.length / clientGoal) * 100, 100) : 0;

  const handleShortcut = (path: string) => {
    navigate(path, { state: { openModal: true } });
  };

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-7xl mx-auto flex flex-col gap-6 md:gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight mb-1">Visão Geral</h2>
          <p className="text-text-secondary font-bold text-base md:text-lg">Central de comando operacional e financeira.</p>
        </div>
        <div className="flex items-center gap-2 text-[10px] md:text-xs font-black text-text-secondary bg-white px-4 py-2.5 md:px-5 md:py-3 rounded-2xl border border-gray-100 shadow-sm uppercase tracking-widest w-fit">
          <span className="material-symbols-outlined text-[16px] md:text-[18px]">calendar_today</span>
          <span>Janeiro 2026</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-50 shadow-sm group hover:border-primary/20 transition-all">
          <p className="text-[9px] md:text-[10px] font-black text-text-secondary uppercase tracking-[0.2em]">Clientes na Base</p>
          <p className="text-3xl md:text-4xl font-black text-text-main mt-3 md:mt-4 tracking-tighter">{clients.length}</p>
          <div className="mt-5 w-full h-1.5 bg-gray-50 rounded-full overflow-hidden">
            <div
              className="bg-blue-500 h-full rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(59,130,246,0.2)]"
              style={{ width: `${clientProgress}%` }}
            ></div>
          </div>
          <p className="text-[8px] font-bold text-gray-400 uppercase mt-2 tracking-widest">Meta: {clientGoal}</p>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-50 shadow-sm group hover:border-primary/20 transition-all">
          <p className="text-[9px] md:text-[10px] font-black text-text-secondary uppercase tracking-[0.2em]">Fluxo Operacional</p>
          <p className="text-3xl md:text-4xl font-black text-text-main mt-3 md:mt-4 tracking-tighter">{activeProjects} <span className="text-lg text-text-secondary font-bold">Ativos</span></p>
          <p className="text-[10px] md:text-[11px] font-bold text-orange-500 mt-4 uppercase tracking-wider">{pendingTasks} Tarefas Pendentes</p>
        </div>

        <div className="bg-text-main p-6 md:p-8 rounded-[23px] shadow-xl shadow-text-main/10 text-white flex flex-col justify-between min-h-[140px]">
          <p className="text-[9px] md:text-[10px] font-black text-white/50 uppercase tracking-[0.2em]">Saldo em Caixa (R$)</p>
          <p className="text-3xl md:text-4xl font-black tracking-tighter mt-3 md:mt-4">R$ {balance.toLocaleString('pt-BR')}</p>
          <div className="flex items-center gap-2 text-[9px] md:text-[10px] font-black text-green-400 mt-4 uppercase tracking-widest">
            <span className="material-symbols-outlined text-[14px] md:text-[16px]">verified</span> Saudável
          </div>
        </div>

        <div className="bg-primary p-6 md:p-8 rounded-[23px] shadow-2xl shadow-primary/20 text-white flex flex-col justify-between min-h-[140px]">
          <p className="text-[9px] md:text-[10px] font-black text-white/50 uppercase tracking-[0.2em]">Faturamento Mês</p>
          <p className="text-3xl md:text-4xl font-black tracking-tighter mt-3 md:mt-4">R$ {totalRevenue.toLocaleString('pt-BR')}</p>
          <button onClick={() => navigate('/financials')} className="mt-4 w-full py-2.5 md:py-3 bg-white/10 hover:bg-white/20 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-widest transition-all">Ver Detalhes</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2 bg-white rounded-[23px] border border-gray-50 shadow-sm p-6 md:p-10 overflow-hidden">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <h3 className="text-xl md:text-2xl font-black text-text-main tracking-tight">Performance Operacional</h3>
            <button onClick={() => navigate('/projects')} className="text-[9px] md:text-[10px] font-black text-primary uppercase tracking-widest hover:underline">Ver Todos</button>
          </div>

          <div className="w-full">
            <table className="w-full text-left table-fixed">
              <thead>
                <tr className="text-[9px] md:text-[10px] text-text-secondary border-b border-gray-50 uppercase font-black tracking-widest">
                  <th className="pb-4 md:pb-6 w-[45%] sm:w-auto">Projeto</th>
                  <th className="pb-4 md:pb-6 hidden sm:table-cell">Cliente</th>
                  <th className="pb-4 md:pb-6 hidden md:table-cell">Status</th>
                  <th className="pb-4 md:pb-6 text-right w-[55%] sm:w-auto">Saúde</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {projects.slice(0, 5).map((p) => (
                  <tr key={p.id} className="group hover:bg-gray-50/50 transition-colors border-b border-gray-50/30 last:border-0">
                    <td className="py-4 md:py-6 font-black text-text-main truncate pr-2">{p.name}</td>
                    <td className="py-4 md:py-6 text-text-secondary font-bold text-[10px] uppercase tracking-wider hidden sm:table-cell truncate">{p.client}</td>
                    <td className="py-4 md:py-6 hidden md:table-cell">
                      <span className={`text-[8px] md:text-[9px] font-black px-2 py-0.5 md:px-2.5 md:py-1 rounded-full uppercase tracking-wider ${p.status === 'Active' ? 'bg-primary-light text-primary' : 'bg-gray-100 text-gray-500'
                        }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-4 md:py-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="flex-1 max-w-[80px] sm:max-w-none bg-gray-50 h-1.5 rounded-full overflow-hidden">
                          <div className="bg-primary h-full rounded-full transition-all duration-500 shadow-[0_0_4px_rgba(138,0,73,0.1)]" style={{ width: `${p.completion}%` }}></div>
                        </div>
                        <span className="text-[9px] md:text-[10px] font-black text-primary w-8 shrink-0">{p.completion}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-[23px] border border-gray-50 shadow-sm p-6 md:p-10 flex flex-col">
          <h3 className="text-xl md:text-2xl font-black text-text-main tracking-tight mb-8 md:mb-10">Acesso Rápido</h3>
          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={() => handleShortcut('/clients')}
              className="flex items-center gap-4 md:gap-5 p-5 md:p-6 rounded-[23px] border border-gray-50 hover:border-primary/20 hover:bg-primary/5 transition-all text-left group"
            >
              <div className="size-10 md:size-12 rounded-xl md:rounded-2xl bg-primary-light text-primary flex items-center justify-center transition-transform group-hover:scale-110 shrink-0">
                <span className="material-symbols-outlined text-[20px] md:text-[24px]">person_add</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black truncate">Adicionar Cliente</p>
                <p className="text-[9px] md:text-[10px] text-text-secondary font-bold uppercase tracking-widest mt-1 truncate">Novo registro na base</p>
              </div>
            </button>
            <button
              onClick={() => handleShortcut('/financials')}
              className="flex items-center gap-4 md:gap-5 p-5 md:p-6 rounded-[23px] border border-gray-50 hover:border-primary/20 hover:bg-primary/5 transition-all text-left group"
            >
              <div className="size-10 md:size-12 rounded-xl md:rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center transition-transform group-hover:scale-110 shrink-0">
                <span className="material-symbols-outlined text-[20px] md:text-[24px]">request_quote</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black truncate">Gerar Cobrança</p>
                <p className="text-[9px] md:text-[10px] text-text-secondary font-bold uppercase tracking-widest mt-1 truncate">Contas a receber</p>
              </div>
            </button>
            <button
              onClick={() => handleShortcut('/kanban')}
              className="flex items-center gap-4 md:gap-5 p-5 md:p-6 rounded-[23px] border border-gray-50 hover:border-primary/20 hover:bg-primary/5 transition-all text-left group"
            >
              <div className="size-10 md:size-12 rounded-xl md:rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center transition-transform group-hover:scale-110 shrink-0">
                <span className="material-symbols-outlined text-[20px] md:text-[24px]">add_task</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black truncate">Nova Demanda</p>
                <p className="text-[9px] md:text-[10px] text-text-secondary font-bold uppercase tracking-widest mt-1 truncate">Ir para o Kanban</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
